import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import usersService from '../services/usersService';
import localDB from '../services/localDB';
import { toast } from '../utils/toast';
import { integrationsService } from '../services/integrationsService';
import { carryItemsService } from '../services/carryItemsService';
import { useSettings } from '../services/SettingsContext';
import { DEFAULT_CLIENT_FIELDS, DEFAULT_OBJECT_FIELDS } from '../utils/profileFields';
import './Settings.css';

const CONFIRM_WORD = 'УДАЛИТЬ';

const CLIENT_FIELD_LABELS = {
  phone: 'Телефон',
  email: 'Электронная почта',
  company: 'Компания',
  notes: 'Заметки',
  rating: 'Рейтинг',
};

const OBJECT_FIELD_LABELS = {
  client: 'Клиент',
  address: 'Адрес и переход в Яндекс Карты',
  metro: 'Метро',
  contact_name: 'Контакт на объекте',
  contact_phone: 'Телефон и кнопка звонка',
  contact_email: 'Электронная почта',
  work_hours: 'Режим работы',
  description: 'Описание',
  access_notes: 'Доступ и важные нюансы',
  systems: 'Системы на объекте',
  service: 'Параметры обслуживания',
};

const Settings = () => {
  const { updateSettings: updateGlobalSettings } = useSettings();
  const [clearAllStep, setClearAllStep] = useState(0);
  const [clearConfirmInput, setClearConfirmInput] = useState('');
  const [clearAllLoading, setClearAllLoading] = useState(false);
  const [settings, setSettings] = useState({
    show_expenses: true,
    show_address: true,
    show_client: true,
    show_cost: true,
    tax_rate: 7,
    sounds_enabled: false,
    animations_enabled: true,
    client_fields: { ...DEFAULT_CLIENT_FIELDS },
    object_fields: { ...DEFAULT_OBJECT_FIELDS }
  });
  const [integrations, setIntegrations] = useState({
    telegram_enabled: false, telegram_chat_id: '', telegram_bot_token: '', telegram_token_configured: false,
    max_enabled: false, max_push_url: '', max_bridge_secret: '', max_secret_configured: false,
    delivery_mode: 'both', notify_new_tasks: true, notify_daily_report: true, notify_monthly_report: true,
  });
  const [integrationBusy, setIntegrationBusy] = useState(false);
  const [carryTemplates, setCarryTemplates] = useState([]);
  const [newCarryItem, setNewCarryItem] = useState('');
  const [loading, setLoading] = useState(true);

  const confirmTwice = (msg1, msg2) => {
    if (!window.confirm(msg1)) return false;
    if (!window.confirm(msg2)) return false;
    return true;
  };

  const resetIncome = async () => {
    if (!confirmTwice('Обнулить все доходы?', 'Подтверждаете безвозвратное удаление всех доходов? Сделайте бэкап.')) return;
    try {
      const res = await adminService.resetIncome();
      toast.success(`Доходы обнулены. Удалено: ${res.data.deleted || 0}`);
    } catch (e) {
      toast.error('Не удалось обнулить доходы');
    }
  };

  const startClearAll = () => setClearAllStep(1);
  const cancelClearAll = () => { setClearAllStep(0); setClearConfirmInput(''); };

  const confirmClearAllStep = () => {
    if (clearAllStep === 1) setClearAllStep(2);
    else if (clearAllStep === 2) setClearAllStep(3);
  };

  const executeClearAll = async () => {
    if (clearConfirmInput.trim() !== CONFIRM_WORD) return;
    setClearAllLoading(true);
    try {
      await adminService.clearAllData();
      try {
        await localDB.clearAllUserData();
      } catch (e) {
        console.warn('Local DB clear warning:', e);
      }
      toast.success('Все данные очищены');
      cancelClearAll();
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Не удалось очистить данные');
    } finally {
      setClearAllLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    integrationsService.get().then(r => setIntegrations(prev => ({ ...prev, ...(r.data || {}) }))).catch(() => {});
    loadCarryTemplates();
  }, []);

  const loadCarryTemplates = async () => {
    try { setCarryTemplates((await carryItemsService.getAll()).data || []); } catch (_) {}
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await usersService.getSettings();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      const newSettings = { ...settings, [key]: value };
      setSettings(newSettings);
      await updateGlobalSettings({ [key]: value });
      toast.success('Настройки сохранены');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Ошибка сохранения настроек');
      // Откатываем изменения
      loadSettings();
    }
  };

  const saveIntegrations = async () => {
    setIntegrationBusy(true);
    try {
      const { telegram_token_configured, max_secret_configured, ...payload } = integrations;
      const response = await integrationsService.update(payload);
      setIntegrations(prev => ({ ...prev, telegram_bot_token: '', max_bridge_secret: '', ...(response.data || {}) }));
      toast.success('Настройки ботов сохранены');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось сохранить настройки ботов');
    } finally { setIntegrationBusy(false); }
  };

  const updateProfileField = (group, defaults, key, value) => {
    const next = { ...defaults, ...(settings[group] || {}), [key]: value };
    updateSetting(group, next);
  };

  const testIntegration = async (channel) => {
    setIntegrationBusy(true);
    try {
      await integrationsService.test(channel);
      toast.success(`Тест ${channel === 'telegram' ? 'Telegram' : 'MAX'} отправлен`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Тест не прошёл');
    } finally { setIntegrationBusy(false); }
  };

  const addCarryTemplate = async () => {
    const value = newCarryItem.trim();
    if (!value) return;
    try {
      await carryItemsService.create(value);
      setNewCarryItem('');
      await loadCarryTemplates();
    } catch (error) { toast.error(error.response?.data?.error || 'Не удалось добавить пункт'); }
  };

  const ToggleSwitch = ({ label, checked, onChange, description }) => (
    <div className="settings-toggle-row">
      <div className="settings-toggle-copy">
        <div style={{ fontWeight: 500, marginBottom: 4 }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: '#666' }}>{description}</div>}
      </div>
      <label className="settings-switch">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span style={{
          position: 'absolute',
          cursor: 'pointer',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: checked ? '#4CAF50' : '#ccc',
          transition: '.4s',
          borderRadius: 26,
        }}>
          <span style={{
            position: 'absolute',
            content: '""',
            height: 20,
            width: 20,
            left: checked ? 26 : 3,
            bottom: 3,
            backgroundColor: 'white',
            transition: '.4s',
            borderRadius: '50%',
          }} />
        </span>
      </label>
    </div>
  );

  return (
    <div className="page settings-page">
      <h1>⚙️ Настройки</h1>
      <div className="card" style={{ marginTop: 12 }}>
        <h2>Управление данными</h2>
        <p>Эти действия затрагивают только ваши данные.</p>
        <div className="settings-action-row">
          <button className="danger-btn" onClick={resetIncome}>Обнулить доходы</button>
          <button className="danger-btn" type="button" onClick={startClearAll} style={{ background: '#c62828' }}>Очистить все данные</button>
        </div>
        <p style={{ marginTop: 8, color: '#b00' }}>
          Внимание: действия необратимы. Перед выполнением рекомендуется сделать резервную копию.
        </p>
      </div>

      {clearAllStep >= 1 && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={cancelClearAll}>
          <div className="card" style={{ maxWidth: 420, width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Очистить все данные</h2>
            {clearAllStep === 1 && (
              <>
                <p>Будет удалено безвозвратно:</p>
                <ul style={{ textAlign: 'left', marginBottom: 16 }}>
                  <li>все приходы и расходы (финансы);</li>
                  <li>все отчёты и задачи;</li>
                  <li>все клиенты;</li>
                  <li>заметки, проекты, шаблоны.</li>
                </ul>
                <p style={{ color: '#b00' }}>Продолжить?</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={cancelClearAll}>Отмена</button>
                  <button type="button" className="danger-btn" onClick={confirmClearAllStep}>Продолжить</button>
                </div>
              </>
            )}
            {clearAllStep === 2 && (
              <>
                <p style={{ color: '#b00', fontWeight: 600 }}>Подтвердите ещё раз:</p>
                <p>Все ваши финансы, задачи, клиенты и связанные данные будут безвозвратно удалены. Отменить действие будет невозможно.</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={cancelClearAll}>Отмена</button>
                  <button type="button" className="danger-btn" onClick={confirmClearAllStep}>Подтверждаю</button>
                </div>
              </>
            )}
            {clearAllStep === 3 && (
              <>
                <p>Введите слово <strong>{CONFIRM_WORD}</strong> для окончательного подтверждения:</p>
                <input
                  type="text"
                  value={clearConfirmInput}
                  onChange={(e) => setClearConfirmInput(e.target.value)}
                  placeholder={CONFIRM_WORD}
                  className="input"
                  style={{ width: '100%', marginBottom: 16, boxSizing: 'border-box' }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={cancelClearAll}>Отмена</button>
                  <button
                    type="button"
                    className="danger-btn"
                    disabled={clearConfirmInput.trim() !== CONFIRM_WORD || clearAllLoading}
                    onClick={executeClearAll}
                    style={{ background: '#c62828' }}
                  >
                    {clearAllLoading ? 'Очистка…' : 'Очистить всё'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ marginTop: 12 }}>
          <p>Загрузка настроек...</p>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginTop: 12 }}>
            <h2>Налог и эффекты</h2>
            <label style={{ display: 'block', marginBottom: 14 }}>
              <span style={{ display: 'block', marginBottom: 6 }}>Ставка налога, %</span>
              <input
                className="input"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.tax_rate ?? 7}
                onChange={(e) => setSettings(prev => ({ ...prev, tax_rate: e.target.value }))}
                onBlur={(e) => updateSetting('tax_rate', Number(e.target.value || 0))}
                style={{ maxWidth: 140 }}
              />
            </label>
            <ToggleSwitch label="Звуки" checked={Boolean(settings.sounds_enabled)} onChange={(value) => updateSetting('sounds_enabled', value)} description="Короткий сигнал при завершении задачи" />
            <ToggleSwitch label="Анимации" checked={settings.animations_enabled !== false} onChange={(value) => updateSetting('animations_enabled', value)} description="Переходы и анимация завершения; звук настраивается отдельно" />
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h2>Telegram и MAX</h2>
            <p>Настройки сохраняются в CRM. Токены и секреты зашифрованы и обратно не показываются.</p>
            <ToggleSwitch label="Telegram" checked={integrations.telegram_enabled} onChange={(value) => setIntegrations(prev => ({ ...prev, telegram_enabled: value }))} description="Уведомления о задачах и отчёты" />
            <div style={{ display: 'grid', gap: 10, margin: '12px 0' }}>
              <input className="input" value={integrations.telegram_bot_token || ''} onChange={(e) => setIntegrations(prev => ({ ...prev, telegram_bot_token: e.target.value }))} placeholder={integrations.telegram_token_configured ? 'Токен сохранён — введите только для замены' : 'Токен от @BotFather'} type="password" autoComplete="new-password" />
              <input className="input" value={integrations.telegram_chat_id || ''} onChange={(e) => setIntegrations(prev => ({ ...prev, telegram_chat_id: e.target.value }))} placeholder="Chat ID" />
            </div>
            <ToggleSwitch label="MAX" checked={integrations.max_enabled} onChange={(value) => setIntegrations(prev => ({ ...prev, max_enabled: value }))} description="Работает через URL моста MAX, подходит как резерв за границей" />
            <div style={{ display: 'grid', gap: 10, margin: '12px 0' }}>
              <input className="input" value={integrations.max_push_url || ''} onChange={(e) => setIntegrations(prev => ({ ...prev, max_push_url: e.target.value }))} placeholder="https://max-bot.example/api/crm/push" />
              <input className="input" value={integrations.max_bridge_secret || ''} onChange={(e) => setIntegrations(prev => ({ ...prev, max_bridge_secret: e.target.value }))} placeholder={integrations.max_secret_configured ? 'Секрет сохранён — введите только для замены' : 'Общий секрет моста'} type="password" autoComplete="new-password" />
              <select className="input" value={integrations.delivery_mode} onChange={(e) => setIntegrations(prev => ({ ...prev, delivery_mode: e.target.value }))}>
                <option value="both">Отправлять в оба</option>
                <option value="telegram_first">Telegram, MAX при ошибке</option>
                <option value="max_first">MAX, Telegram при ошибке</option>
              </select>
            </div>
            <ToggleSwitch label="Новые задачи" checked={integrations.notify_new_tasks} onChange={(value) => setIntegrations(prev => ({ ...prev, notify_new_tasks: value }))} />
            <ToggleSwitch label="Ежедневный отчёт" checked={integrations.notify_daily_report} onChange={(value) => setIntegrations(prev => ({ ...prev, notify_daily_report: value }))} />
            <ToggleSwitch label="Месячный отчёт" checked={integrations.notify_monthly_report} onChange={(value) => setIntegrations(prev => ({ ...prev, notify_monthly_report: value }))} />
            <div className="settings-action-row settings-action-row-spaced">
              <button className="btn btn-primary" type="button" disabled={integrationBusy} onClick={saveIntegrations}>Сохранить</button>
              <button className="btn btn-secondary" type="button" disabled={integrationBusy || !integrations.telegram_token_configured} onClick={() => testIntegration('telegram')}>Тест Telegram</button>
              <button className="btn btn-secondary" type="button" disabled={integrationBusy || !integrations.max_secret_configured} onClick={() => testIntegration('max')}>Тест MAX</button>
            </div>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h2>«Не забыть с собой»</h2>
            <p>Настройте постоянный список; в задаче нужные пункты добавляются одним нажатием.</p>
            <div className="settings-inline-form">
              <input className="input" value={newCarryItem} onChange={(e) => setNewCarryItem(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCarryTemplate(); } }} placeholder="Например: тестер" style={{ flex: 1 }} />
              <button className="btn btn-primary" type="button" onClick={addCarryTemplate}>Добавить</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {carryTemplates.map(item => (
                <button key={item.id} type="button" className="btn btn-secondary" onClick={async () => { await carryItemsService.delete(item.id); loadCarryTemplates(); }} title="Удалить">
                  {item.name} ×
                </button>
              ))}
            </div>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h2>Отображение полей</h2>
            <p>Выберите, какие поля показывать в формах задач:</p>
            <ToggleSwitch
              label="Показывать расходы"
              checked={settings.show_expenses}
              onChange={(value) => updateSetting('show_expenses', value)}
              description="Показывать блок расходов при создании/редактировании задач"
            />
            <ToggleSwitch
              label="Показывать адрес"
              checked={settings.show_address}
              onChange={(value) => updateSetting('show_address', value)}
              description="Показывать поле адреса при создании/редактировании задач"
            />
            <ToggleSwitch
              label="Показывать клиента"
              checked={settings.show_client}
              onChange={(value) => updateSetting('show_client', value)}
              description="Показывать поле выбора клиента при создании/редактировании задач"
            />
            <ToggleSwitch
              label="Показывать стоимость"
              checked={settings.show_cost}
              onChange={(value) => updateSetting('show_cost', value)}
              description="Показывать поле стоимости задачи"
            />
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h2>Карточка клиента</h2>
            <p>Имя показывается всегда. Остальные поля можно включать отдельно. Почта по умолчанию скрыта, но данные не удаляются.</p>
            {Object.entries(CLIENT_FIELD_LABELS).map(([key, label]) => (
              <ToggleSwitch
                key={key}
                label={label}
                checked={{ ...DEFAULT_CLIENT_FIELDS, ...(settings.client_fields || {}) }[key] !== false}
                onChange={(value) => updateProfileField('client_fields', DEFAULT_CLIENT_FIELDS, key, value)}
              />
            ))}
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h2>Паспорт объекта</h2>
            <p>Название, статус и финансовая сводка показываются всегда. Остальные поля настраиваются.</p>
            {Object.entries(OBJECT_FIELD_LABELS).map(([key, label]) => (
              <ToggleSwitch
                key={key}
                label={label}
                checked={{ ...DEFAULT_OBJECT_FIELDS, ...(settings.object_fields || {}) }[key] !== false}
                onChange={(value) => updateProfileField('object_fields', DEFAULT_OBJECT_FIELDS, key, value)}
              />
            ))}
          </div>

        </>
      )}
    </div>
  );
};

export default Settings;
